import { useState, useEffect } from "react";
import { supabase, BRAND, BRAND_LIGHT, TEXT, LINE, NEUTRAL_BG, unreadBadge, errorText, savedText, text, SCRIM, ROW_LINE, INK, MUTED, FAINT, WHITE, HOVER_BG, SURFACE, SHADOW_SHEET } from "./constants";
import { Icon, CloseButton } from "./shared";
// lucide-react: licencia ISC, uso comercial libre y sin atribución.
// Mismo juego de iconos que el portal, para que las dos apps se vean iguales.
import {
  LayoutDashboard, Bell, Briefcase, Compass, MessageSquare, Settings,
  LogOut, Wrench, MoreHorizontal, ChevronRight, Building2, Award,
  Pencil,
  MessageSquarePlus,
} from "lucide-react";
import ImageCropModal from "./ImageCropModal";
import { useIsMobile, MOBILE_TOP, MOBILE_BOTTOM } from "./lib/responsive";
import Logo from "./Logo";

const NotifBadge = ({ count }) => (
  <span style={{ ...unreadBadge, marginLeft: 4 }}>
    {count}
  </span>
);

const Sidebar = ({ view, setView, t, lang, setLang, companyName, candidateStatuses = {}, openPositionsList = [], seenReports = new Set(), setSeenReports = () => {}, isAdmin = false, companyLogoUrl = null, setCompanyLogoUrl = () => {}, clients = [], selectedClientId = null, setSelectedClientId = () => {}, notifRefreshKey = 0, onShowOnboarding = () => {}, onFeedback = () => {}, chatUnread = 0, notifCount = 0, onContactRecruiter = () => {} }) => {
  const [logoCropSrc, setLogoCropSrc] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  // Aviso del logo (23/10): antes el guardado fallaba en silencio
  const [sideMsg, setSideMsg] = useState(null); // { ok, text }
  // Notificaciones y registro de accesos viven en el Panel de admin (26/10).
  // Aquí solo el contador de no leídas, que va en "Panel de admin". Se vuelve
  // a contar al entrar y al salir del panel (el panel las marca como leídas).
  const [unreadCount, setUnreadCount] = useState(0);
  // Móvil: barra superior + barra inferior + hoja "Más" (mismo patrón que el
  // portal). La hoja recuerda en qué vista se abrió: al cambiar de vista deja
  // de verse sola.
  const isMobile = useIsMobile();
  const [moreAt, setMoreAt] = useState(null);
  const inAdmin = view === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    supabase.from("admin_notifications").select("id", { count: "exact", head: true })
      .neq("type", "login").eq("read", false)
      .then(({ count }) => setUnreadCount(count || 0));
  }, [isAdmin, notifRefreshKey, inAdmin]);
  // Dentro del panel ya se están viendo: sin contador
  const adminBadge = inAdmin ? 0 : unreadCount;

  // La burbuja roja va en Notificaciones (03/11): cuenta los avisos de
  // lib/todos.js. Posiciones ya no lleva número.

  // «Posiciones» abre siempre la lista (02/11, decisión de Borja): el atajo
  // a la única posición abierta despistaba. Las posiciones también están en la Home.
  // Dentro de una posición o de una ficha, "Posiciones" sigue marcada
  const POSITION_VIEWS = ["positions", "open", "closed", "deleted", "candidates", "candidateDetail", "newPosition"];
  const isActive = (id) => id === "positions" ? POSITION_VIEWS.includes(view) : view === id;
  const navItems = [
    { id: "dashboard", label: t.dashboard, icon: LayoutDashboard },
    { id: "notifications", label: lang === "es" ? "Notificaciones" : "Notifications", icon: Bell, badge: notifCount },
    { id: "positions", label: lang === "es" ? "Posiciones" : "Positions", icon: Briefcase },
    { id: "contactRecruiter", label: lang === "es" ? "Chat con recruiter" : "Recruiter chat", icon: MessageSquare, badge: chatUnread, onClick: onContactRecruiter },
  ];

  // ── MÓVIL ──────────────────────────────────────────────────────────────
  if (isMobile) {
    const es = lang === "es";
    const moreOpen = moreAt === view;
    const closeMore = () => setMoreAt(null);
    const go = (v) => { setView(v); setMoreAt(null); };
    const MORE_VIEWS = ["account", "support", "admin", "clients", "credentials", "documents", "cultureQuestionnaire"];
    const moreActive = MORE_VIEWS.includes(view);
    const isPositions = ["positions", "open", "closed", "deleted", "candidates", "candidateDetail", "newPosition"].includes(view);
    const tabs = [
      { key: "dashboard", icon: LayoutDashboard, label: es ? "Inicio" : "Home", active: view === "dashboard", onClick: () => go("dashboard") },
      { key: "notifications", icon: Bell, label: es ? "Avisos" : "Alerts", active: view === "notifications", badge: notifCount, onClick: () => go("notifications") },
      { key: "positions", icon: Briefcase, label: es ? "Posiciones" : "Positions", active: isPositions, onClick: () => go("positions") },
      { key: "chat", icon: MessageSquare, label: "Chat", active: false, badge: chatUnread, onClick: () => { setMoreAt(null); onContactRecruiter(); } },
    ];
    const row = { width: "100%", display: "flex", alignItems: "center", gap: 12, minHeight: 52, padding: "0 20px", background: "none", border: "none", borderTop: `1px solid ${ROW_LINE}`, cursor: "pointer", fontFamily: "inherit", fontSize: 15, fontWeight: 500, color: INK, textAlign: "left" };
    const sheetItem = (key, Ico, label, onClick, active, extra) => (
      <button key={key} type="button" onClick={onClick} style={{ ...row, color: active ? BRAND : INK }}>
        <Ico size={20} strokeWidth={1.75} color={active ? BRAND : MUTED} />
        <span style={{ flex: 1 }}>{label}</span>
        {extra || <ChevronRight size={18} color={FAINT} />}
      </button>
    );
    const badge = (n) => n > 0 ? <span style={unreadBadge}>{n}</span> : null;
    const tabBtn = (it) => (
      <button key={it.key} type="button" onClick={it.onClick}
        style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, background: "none", border: "none", fontFamily: "inherit", cursor: "pointer", color: it.active ? BRAND : MUTED, fontSize: 11, fontWeight: 600, position: "relative" }}>
        <it.icon size={22} strokeWidth={1.75} color={it.active ? BRAND : MUTED} />
        {it.label}
        {it.badge > 0 && <span style={{ position: "absolute", top: 6, left: "50%", marginLeft: 6 }}>{badge(it.badge)}</span>}
      </button>
    );

    return (
      <>
        {/* Barra superior: logo de Rutment y, a la derecha, el de la empresa */}
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: MOBILE_TOP, paddingTop: "env(safe-area-inset-top)", boxSizing: "content-box", background: WHITE, borderBottom: `1px solid ${LINE}`, display: "flex", alignItems: "center", justifyContent: "space-between", paddingLeft: 16, paddingRight: 16, zIndex: 90 }}>
          {/* El logo lleva a la Home */}
          <button onClick={() => setView("dashboard")} aria-label="Home" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", lineHeight: 0 }}><Logo size={18} /></button>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: INK, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{companyName}</span>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: NEUTRAL_BG, border: "1.5px solid " + LINE, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
              {companyLogoUrl ? <img src={companyLogoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", padding: 3 }} /> : <Icon name="user" size={14} color={TEXT} />}
            </div>
          </div>
        </div>

        {/* Hoja "Más" */}
        {moreOpen && (
          <>
            <div onClick={closeMore} style={{ position: "fixed", inset: 0, background: SCRIM, zIndex: 95 }} />
            <div role="dialog" aria-label={es ? "Más" : "More"}
              style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 96, background: WHITE, borderRadius: "16px 16px 0 0", paddingBottom: `calc(${MOBILE_BOTTOM}px + env(safe-area-inset-bottom))`, maxHeight: "85vh", overflowY: "auto", boxShadow: SHADOW_SHEET }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px 12px" }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: INK }}>{t.hi}, {companyName}!</span>
                <CloseButton onClick={closeMore} label={es ? "Cerrar" : "Close"} />
              </div>

              {isAdmin && clients.length > 0 && (
                <div style={{ padding: "4px 20px 14px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>{es ? "Cliente visible" : "Viewing client"}</div>
                  <select value={selectedClientId || ""} onChange={e => setSelectedClientId(Number(e.target.value))}
                    style={{ width: "100%", height: 44, fontSize: 16, fontWeight: 600, color: INK, border: `1.5px solid ${LINE}`, borderRadius: 8, padding: "0 12px", background: WHITE, fontFamily: "inherit" }}>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}

              {sheetItem("account", Settings, t.manageAccount, () => go("account"), view === "account")}
              {sheetItem("support", Compass, es ? "Metodología" : "Methodology", () => go("support"), view === "support")}
              {!isAdmin && sheetItem("feedback", MessageSquarePlus, es ? "Enviar comentarios" : "Send feedback", () => { closeMore(); onFeedback(); }, false)}

              {isAdmin && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, padding: "16px 20px 8px", borderTop: `1px solid ${LINE}` }}>Admin</div>
                  {sheetItem("clients", Building2, es ? "Clientes" : "Clients", () => go("clients"), view === "clients")}
                  {sheetItem("credentials", Award, "Credentials", () => go("credentials"), view === "credentials")}
                  {sheetItem("admin", Wrench, es ? "Panel de admin" : "Admin panel", () => go("admin"), view === "admin", badge(adminBadge))}
                </>
              )}

              <div style={{ ...row, cursor: "default", justifyContent: "space-between" }}>
                <span style={{ fontSize: 13, color: TEXT }}>{es ? "Idioma" : "Language"}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  {["en", "es"].map(l => (
                    <button key={l} type="button" onClick={() => setLang(l)} style={{ minHeight: 40, padding: "0 16px", borderRadius: 8, border: `1.5px solid ${lang === l ? BRAND : LINE}`, background: lang === l ? BRAND : WHITE, color: lang === l ? WHITE : TEXT, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{l.toUpperCase()}</button>
                  ))}
                </div>
              </div>
              <button type="button" onClick={() => supabase.auth.signOut()} style={{ ...row, color: MUTED }}>
                <LogOut size={20} strokeWidth={1.75} color={FAINT} />
                {es ? "Cerrar sesión" : "Log out"}
              </button>
            </div>
          </>
        )}

        {/* Barra inferior */}
        <nav style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 97, background: WHITE, borderTop: `1px solid ${LINE}`, display: "flex", height: MOBILE_BOTTOM, paddingBottom: "env(safe-area-inset-bottom)", boxSizing: "content-box" }}>
          {tabs.map(it => tabBtn({ ...it, active: it.active && !moreOpen }))}
          {tabBtn({ key: "more", icon: MoreHorizontal, label: es ? "Más" : "More", active: moreActive || moreOpen, badge: isAdmin ? adminBadge : 0, onClick: () => setMoreAt(moreOpen ? null : view) })}
        </nav>
      </>
    );
  }

  // ── ESCRITORIO ─────────────────────────────────────────────────────────
  return (
    <div style={{ width: 240, minWidth: 240, background: WHITE, borderRight: `1px solid ${LINE}`, display: "flex", flexDirection: "column", height: "100vh", position: "sticky", top: 0, overflowY: "auto" }}>
      {/* Logo */}
      <div style={{ padding: "24px 24px 16px", borderBottom: `1px solid ${ROW_LINE}` }}>
        <button onClick={() => setView("dashboard")} aria-label="Home" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", lineHeight: 0 }}><Logo size={20} /></button>
        <div style={{ fontSize: 12, fontWeight: 500, color: MUTED, marginTop: 6, lineHeight: 1.3 }}>{lang === "es" ? "Fichajes clave para founders" : "Key hires for founders"}</div>
      </div>

      {/* User greeting */}
      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${ROW_LINE}` }}>
        <div style={{ position: "relative", flexShrink: 0 }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: NEUTRAL_BG, border: "1.5px solid " + LINE, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
            {companyLogoUrl ? <img src={companyLogoUrl} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain", padding: 5 }} /> : <Icon name="user" size={20} color={TEXT} />}
          </div>
          {isAdmin && (
            <button onClick={() => document.getElementById("company-logo-input").click()}
              title={lang === "es" ? "Subir logo" : "Upload logo"}
              style={{ position: "absolute", bottom: -2, right: -2, width: 18, height: 18, borderRadius: "50%", background: BRAND, border: `2px solid ${WHITE}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: WHITE, padding: 0 }}><Pencil size={10} strokeWidth={2} /></button>
          )}
          <input id="company-logo-input" type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
            const file = e.target.files[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = ev => setLogoCropSrc(ev.target.result);
            reader.readAsDataURL(file);
            e.target.value = "";
          }} />
          {logoCropSrc && <ImageCropModal lang={lang} imageSrc={logoCropSrc} uploading={logoUploading} onCancel={() => setLogoCropSrc(null)} onConfirm={async blob => {
            setLogoUploading(true);
            try {
              const path = `company-logo-${Date.now()}.png`;
              const { error } = await supabase.storage.from("candidate-photos").upload(path, blob, { upsert: true, contentType: "image/png" });
              if (error) throw error;
              const { data } = supabase.storage.from("candidate-photos").getPublicUrl(path);
              if (selectedClientId) {
                const { data: rows, error: upErr } = await supabase.from("clients").update({ logo_url: data.publicUrl }).eq("id", selectedClientId).select("id");
                if (upErr || !rows?.length) throw upErr || new Error("0 filas");
              }
              setCompanyLogoUrl(data.publicUrl);
              setLogoCropSrc(null);
            } catch(err) { console.error(err); setSideMsg({ ok: false, text: lang === "es" ? "No se pudo guardar el logo." : "Couldn't save the logo." }); }
            setLogoUploading(false);
          }} />}
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>{t.hi}, {companyName}!</div>
          {sideMsg && <div style={{ ...(sideMsg.ok ? savedText : errorText), fontSize: 11, marginTop: 4 }}>{sideMsg.text}</div>}
        </div>
      </div>
      {isAdmin && clients.length > 0 && (
        <div style={{ padding: "10px 16px", borderBottom: `1px solid ${ROW_LINE}` }}>
          {/* Solo el selector: Calendly y los accesos viven en la ficha del cliente (24/10) */}
          <div style={{ ...text.label, marginBottom: 6 }}>{lang === "es" ? "Cliente visible" : "Viewing client"}</div>
          <select value={selectedClientId || ""} onChange={e => setSelectedClientId(Number(e.target.value))}
            style={{ width: "100%", fontSize: 12, fontWeight: 600, color: INK, border: `1.5px solid ${LINE}`, borderRadius: 8, padding: "6px 10px", background: HOVER_BG, cursor: "pointer", outline: "none", marginBottom: 4 }}>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      {/* Nav: nunca se encoge (con poca altura llegaba a cero); si no cabe todo, se desplaza el lateral entero */}
      <nav style={{ flex: "1 0 auto", padding: "12px 12px" }}>
        {navItems.filter(item => !item.adminOnly || isAdmin).map(item => (
          <button key={item.id} onClick={() => item.onClick ? item.onClick() : setView(item.id)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 20px 10px 12px", borderRadius: 8, border: "none", cursor: "pointer", background: isActive(item.id) ? BRAND_LIGHT : "transparent", color: isActive(item.id) ? BRAND : TEXT, fontWeight: 600, fontSize: 13, transition: "all 0.15s", marginBottom: 2, textAlign: "left" }}>
            <item.icon size={18} strokeWidth={1.75} color={isActive(item.id) ? BRAND : MUTED} />
            <span style={{ flex: 1 }}>{item.label}</span>
            {item.badge > 0 && <NotifBadge count={item.badge} />}
          </button>
        ))}
      </nav>

      {/* ── ADMIN ────────────────────────────────────────────────────────
             Misma lógica que el portal: línea, cabecera "ADMIN", y debajo las
             secciones en el mismo color que las normales. */}
      {isAdmin && (
        <div style={{ margin: "12px 12px 0", paddingTop: 12, borderTop: `1px solid ${NEUTRAL_BG}` }}>
          <div style={{ ...text.label, padding: "0 12px", marginBottom: 6 }}>Admin</div>
          {/* Memoria de cliente: mismo estilo que las secciones normales */}
          <button onClick={() => setView("clients")} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 20px 10px 12px", borderRadius: 8, border: "none", cursor: "pointer", background: view === "clients" ? BRAND_LIGHT : "transparent", color: view === "clients" ? BRAND : TEXT, fontWeight: 600, fontSize: 13, transition: "all 0.15s", marginBottom: 2, textAlign: "left", fontFamily: "inherit" }}>
            <Building2 size={18} strokeWidth={1.75} color={view === "clients" ? BRAND : MUTED} />
            <span style={{ flex: 1 }}>{lang === "es" ? "Clientes" : "Clients"}</span>
          </button>
          <button onClick={() => setView("credentials")} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 20px 10px 12px", borderRadius: 8, border: "none", cursor: "pointer", background: view === "credentials" ? BRAND_LIGHT : "transparent", color: view === "credentials" ? BRAND : TEXT, fontWeight: 600, fontSize: 13, transition: "all 0.15s", marginBottom: 2, textAlign: "left", fontFamily: "inherit" }}>
            <Award size={18} strokeWidth={1.75} color={view === "credentials" ? BRAND : MUTED} />
            <span style={{ flex: 1 }}>Credentials</span>
          </button>
          {/* Notificaciones y registro de accesos, con el contador de no leídas */}
          <button onClick={() => setView("admin")} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 20px 10px 12px", borderRadius: 8, border: "none", cursor: "pointer", background: view === "admin" ? BRAND_LIGHT : "transparent", color: view === "admin" ? BRAND : TEXT, fontWeight: 600, fontSize: 13, transition: "all 0.15s", marginBottom: 2, textAlign: "left", fontFamily: "inherit" }}>
            <Wrench size={18} strokeWidth={1.75} color={view === "admin" ? BRAND : MUTED} />
            <span style={{ flex: 1 }}>{lang === "es" ? "Panel de admin" : "Admin panel"}</span>
            {adminBadge > 0 && <NotifBadge count={adminBadge} />}
          </button>
        </div>
      )}


      {/* Methodology + How it works: material de consulta, se mira una vez.
          UN SOLO objeto de estilo para los dos. Antes estaban escritos por
          separado y por eso divergían en tamaño y peso — el mismo fallo que
          documentamos y que se repite en cuanto se copia en vez de compartir. */}
      {(() => {
        const footItem = (active) => ({
          width: "100%", display: "flex", alignItems: "center", gap: 10,
          padding: "10px 12px", borderRadius: 8, border: "none", cursor: "pointer",
          background: active ? SURFACE : "transparent",
          color: active ? TEXT : FAINT,
          fontSize: 13, fontWeight: 500, textAlign: "left",
          fontFamily: "inherit", transition: "all 0.15s",
        });
        const items = [
          { key: "account", icon: Settings, label: t.manageAccount, onClick: () => setView("account"), active: view === "account" },
          { key: "support", icon: Compass, label: lang === "es" ? "Metodología" : "Methodology", onClick: () => setView("support"), active: view === "support" },
          // "Send feedback" (20/10): solo clientes, no admin
          ...(!isAdmin ? [{ key: "feedback", icon: MessageSquarePlus, label: lang === "es" ? "Enviar comentarios" : "Send feedback", onClick: onFeedback, active: false }] : []),
          { key: "logout", icon: LogOut, label: lang === "es" ? "Cerrar sesión" : "Log out", onClick: () => supabase.auth.signOut(), active: false },
        ];
        return (
          // Línea encima del bloque común, igual que en el portal
          <div style={{ padding: "12px 12px 8px", borderTop: `1px solid ${ROW_LINE}`, marginTop: "auto" }}>
            {items.map(it => (
              <button key={it.key} onClick={it.onClick} style={footItem(it.active)}
                onMouseEnter={e => { e.currentTarget.style.color = TEXT; e.currentTarget.style.background = SURFACE; }}
                onMouseLeave={e => { if (!it.active) { e.currentTarget.style.color = FAINT; e.currentTarget.style.background = "transparent"; } }}>
                <it.icon size={18} strokeWidth={1.75} color={FAINT} />
                {it.label}
              </button>
            ))}
          </div>
        );
      })()}

      {/* Lang toggle */}
      <div style={{ padding: "16px 20px", borderTop: `1px solid ${ROW_LINE}`, display: "flex", gap: 6 }}>
        {["en", "es"].map(l => (
          <button key={l} onClick={() => setLang(l)} style={{ padding: "5px 12px", borderRadius: 8, border: `1.5px solid ${lang === l ? BRAND : LINE}`, background: lang === l ? BRAND : WHITE, color: lang === l ? WHITE : TEXT, fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.15s" }}>
            {l.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
};




// ─── INLINE AVATAR ───────────────────────────────────────────────────────────

export default Sidebar;
